import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  setAdminUserOpenOrderLimit,
  type AdminOpenOrderLimitResponse,
  type WireAdminOpenOrderLimit,
  type WireAdminUserDetail,
} from '../../api/endpoints.js';
import { useApiMutation } from '../../api/hooks.js';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Field, Input, Modal } from '../../ui/index.js';
import { SoftBreaks } from './SoftBreaks.js';

/**
 * O4 (2026-09-25) — the account's open-order cap (the 429 too_many_pending of
 * POST /api/v1/orders) and how many orders it has open right now. The
 * operator can RAISE the cap for this account (audited PUT
 * /admin/users/:id/open-order-limit, capability user:adjust_balance) or clear
 * the raise. Every number comes from the server: `effective` is shown as
 * sent (max(override, default) on the server), never recomputed here; the
 * dialog's bounds only mirror the server's checks, which stay authoritative.
 */

/** Highest cap the API accepts (OPEN_ORDER_LIMIT_MAX). */
const LIMIT_MAX = 10_000;
/** The API keeps at most this many characters of the audit note. */
const NOTE_MAX = 500;

const fmt = (n: number): string => n.toLocaleString('tr-TR');
/** A cap of 0 means "no cap" on the server. */
const capText = (n: number): string => (n > 0 ? fmt(n) : 'sınırsız');

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

interface KnownLimit {
  readonly override: number | null;
  readonly defaultCap: number;
  readonly effective: number;
  /** The override is the cap in force (above the default). */
  readonly custom: boolean;
}

type LimitView =
  | { readonly kind: 'absent' }
  | { readonly kind: 'unknown'; readonly openOrders: number | null }
  | ({ readonly kind: 'known'; readonly openOrders: number | null } & KnownLimit);

function viewOf(raw: WireAdminOpenOrderLimit | undefined): LimitView {
  if (!raw || typeof raw !== 'object') return { kind: 'absent' };
  const openOrders = isCount(raw.openOrders) ? raw.openOrders : null;
  if (raw.available !== true || !Number.isInteger(raw.default) || !isCount(raw.effective)) {
    return { kind: 'unknown', openOrders };
  }
  const override = isCount(raw.override) && raw.override > 0 ? raw.override : null;
  return {
    kind: 'known',
    openOrders,
    override,
    defaultCap: raw.default,
    effective: raw.effective,
    custom: override !== null && override > raw.default && raw.effective === override,
  };
}

/** "Açık sipariş limiti: …" — the cap in force and where it comes from. */
function LimitValue({ view }: { readonly view: LimitView }): JSX.Element {
  if (view.kind !== 'known') {
    return <>Açık sipariş limiti: <strong>bilinmiyor</strong></>;
  }
  // Tabular digits for a number; "sınırsız" stays in the text face.
  const value = (
    <strong className={view.effective > 0 ? 'uinspect-mono' : undefined}>{capText(view.effective)}</strong>
  );
  if (view.custom) {
    return <>Açık sipariş limiti: {value} (özel; varsayılan {capText(view.defaultCap)})</>;
  }
  if (view.override !== null) {
    // A raise that the default has since caught up with (config raised
    // later): stored, but not in force.
    return (
      <>
        Açık sipariş limiti: {value} (varsayılan; kayıtlı özel limit {fmt(view.override)} etkisiz)
      </>
    );
  }
  return <>Açık sipariş limiti: {value} (varsayılan)</>;
}

function rangeError(defaultCap: number): string {
  return `Varsayılandan (${capText(defaultCap)}) büyük, en fazla ${fmt(LIMIT_MAX)} olan bir tam sayı girin.`;
}

/** The new cap typed in the dialog: an integer above the default, at most 10.000; else null. */
function parseLimit(raw: string, defaultCap: number): number | null {
  const t = raw.trim();
  if (!/^\d{1,6}$/.test(t)) return null;
  const n = Number(t);
  return n > defaultCap && n <= LIMIT_MAX ? n : null;
}

function limitErrorText(reason: string, defaultCap: number): ReactNode {
  switch (reason) {
    case 'limit_out_of_range':
    case 'limit_not_above_default':
      return rangeError(defaultCap);
    case 'default_cap_disabled':
      return 'Varsayılan limit kapalı (sınırsız); özel limit gerekmez.';
    case 'forbidden':
    case 'capability_missing':
      return 'Bu işlem için yetkiniz yok.';
    case 'user_unknown':
      return 'Kullanıcı bulunamadı.';
    case 'limit_store_unavailable':
      return 'Limit kaydı şu an kullanılamıyor (migration 0078 uygulanmamış olabilir).';
    default:
      return (
        <>
          <code>limit</code>: <code><SoftBreaks text={reason} /></code>
        </>
      );
  }
}

const FORM_ID = 'user-open-order-limit-form';

type Mode = 'edit' | 'clear';
type LimitBody = { readonly limit: number | null; readonly reason?: string };

export function OpenOrderLimitCard({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  // The numbers shown must belong to the account the actions target (the
  // route's `actorId`). While the page still shows another account's detail
  // (a stale read after moving from one account to another, or a failed read
  // of the new one), the card reads as unknown and nothing can be sent: a
  // PUT would otherwise change account B while the operator sees account A.
  const sameAccount = user.actorId === actorId;
  const view: LimitView = sameAccount ? viewOf(user.openOrderLimit) : { kind: 'unknown', openOrders: null };
  const known = view.kind === 'known' ? view : null;

  const [mode, setMode] = useState<Mode | null>(null);
  /**
   * The limit as it was when the dialog opened, and the account it was opened
   * for; the values are used while a refresh cannot read the limit.
   */
  const [snapshot, setSnapshot] = useState<{ readonly actorId: string; readonly limit: KnownLimit } | null>(null);
  /** The account the last successful save was for (its banner shows only there). */
  const [savedFor, setSavedFor] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);
  /** `defaultLimit` of a limit_not_above_default answer (the server's default). */
  const serverDefault = useRef<number | null>(null);
  const mut = useApiMutation<LimitBody, AdminOpenOrderLimitResponse>(async (opts, body) => {
    const res = await setAdminUserOpenOrderLimit(opts, actorId, body);
    const d = res.kind === 'error' ? res.body?.defaultLimit : undefined;
    serverDefault.current = typeof d === 'number' && Number.isInteger(d) ? d : null;
    return res;
  });
  const running = mut.state.kind === 'running';

  // Moving to another account closes a dialog that was opened for the previous
  // one and drops the previous result (its banner or error).
  const resetMutation = mut.reset;
  useEffect(() => {
    setMode(null);
    resetMutation();
  }, [actorId, resetMutation]);

  // Live values while the detail reads them, else the values the dialog opened
  // with — and only while the dialog belongs to the account on screen.
  const dialogSnapshot = mode !== null && sameAccount && snapshot?.actorId === actorId ? snapshot : null;
  const ctx: KnownLimit | null = dialogSnapshot ? (known ?? dialogSnapshot.limit) : null;
  const canEdit =
    canAdjust && sameAccount && known !== null && known.defaultCap > 0 && known.defaultCap < LIMIT_MAX;
  const canClear = canAdjust && sameAccount && known !== null;

  function open(next: Mode): void {
    if (!known || !sameAccount) return;
    mut.reset();
    setSnapshot({ actorId, limit: known });
    setDraft(next === 'edit' && known.custom && known.override !== null ? String(known.override) : '');
    setNote('');
    setDirty(false);
    setMode(next);
  }
  function close(): void {
    if (running) return;
    setMode(null);
    mut.reset();
  }

  const parsed = mode === 'edit' && ctx ? parseLimit(draft, ctx.defaultCap) : null;
  const inputInvalid = mode === 'edit' && parsed === null;

  async function onConfirm(ev?: FormEvent): Promise<void> {
    ev?.preventDefault();
    // `ctx` is null unless the dialog was opened for the account on screen and
    // the card shows that account (sameAccount): never a PUT to another one.
    if (!mode || !ctx || running || !canAdjust || !sameAccount) return;
    setDirty(true);
    if (mode === 'edit' && parsed === null) return;
    const limit = mode === 'edit' ? parsed : null;
    const reason = note.trim().slice(0, NOTE_MAX);
    const target = actorId;
    const res = await mut.run(reason ? { limit, reason } : { limit });
    if (res.kind === 'ok') {
      setSavedFor(target);
      setMode(null);
      onChanged();
    } else if (
      res.kind === 'error' &&
      (res.reason === 'limit_not_above_default' ||
        res.reason === 'default_cap_disabled' ||
        res.reason === 'user_unknown')
    ) {
      // The server's view differs from the page (default changed, account
      // gone): refresh so the card shows the current state.
      onChanged();
    }
  }

  // Lowering the cap never cancels anything; say so before the operator confirms.
  const newCap = mode === 'edit' ? parsed : ctx ? Math.max(ctx.defaultCap, 0) : null;
  const lowers = ctx !== null && newCap !== null && ctx.effective > 0 && newCap > 0 && newCap < ctx.effective;

  // The success banner belongs to the account that was saved: never shown on
  // another account's card after the operator moves on.
  const saved =
    mut.state.kind === 'ok' && mode === null && sameAccount && savedFor === actorId
      ? mut.state.data?.data?.user?.openOrderLimit ?? null
      : null;
  const errorDefault = serverDefault.current ?? ctx?.defaultCap ?? 0;
  const atCap =
    view.kind === 'known' && view.effective > 0 && view.openOrders !== null && view.openOrders >= view.effective;

  return (
    <Card flush data-testid="user-open-order-limit-card">
      <CardHeader
        title="Açık sipariş limiti"
        actions={
          known ? (
            known.custom ? (
              <Badge tone="orange" dot>Özel limit</Badge>
            ) : (
              <Badge tone="neutral">Varsayılan</Badge>
            )
          ) : undefined
        }
      />
      <CardBody>
        <div className="uinspect-toggle-row">
          <div className="uinspect-toggle-text">
            <div data-testid="user-open-order-limit-value">
              <LimitValue view={view} />
            </div>
            {view.kind !== 'absent' ? (
              <div className="uinspect-inline-values" data-testid="user-open-order-limit-open">
                <span>
                  Şu an açık sipariş:{' '}
                  {view.openOrders !== null ? (
                    <strong className="uinspect-mono">
                      {fmt(view.openOrders)}
                      {view.kind === 'known' && view.effective > 0 ? ` / ${fmt(view.effective)}` : ''}
                    </strong>
                  ) : (
                    <strong>bilinmiyor</strong>
                  )}
                </span>
                {atCap ? <Badge tone="warn" dot>limit dolu</Badge> : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="uinspect-note">
          {/* No break between the status code and its reason. */}
          Aynı anda açık kalabilecek sipariş sayısı. Site ve API siparişleri birlikte sayılır; limit dolunca
          yeni sipariş 429{' '}too_many_pending ile reddedilir.
        </div>
        <div className="uinspect-actions-row">
          <Button
            variant="secondary"
            size="sm"
            disabled={!canEdit}
            onClick={() => open('edit')}
            data-testid="user-open-order-limit-edit"
          >
            Limiti değiştir
          </Button>
          {known && known.override !== null ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={!canClear}
              onClick={() => open('clear')}
              data-testid="user-open-order-limit-clear"
            >
              Varsayılana dön
            </Button>
          ) : null}
        </div>
        {saved ? (
          <div className="uinspect-feedback">
            <Banner tone="success" testId="user-open-order-limit-saved">
              {saved.override === null
                ? `Özel limit kaldırıldı; hesap varsayılan limitte (${capText(saved.effective)}).`
                : `Açık sipariş limiti kaydedildi: ${capText(saved.effective)}.`}
            </Banner>
          </div>
        ) : null}
      </CardBody>
      <Modal
        open={mode !== null && ctx !== null}
        onClose={close}
        closeOnScrimClick={!running}
        closeOnEsc={!running}
        title={mode === 'clear' ? 'Varsayılana dön' : 'Açık sipariş limitini değiştir'}
        width={480}
        testId="user-open-order-limit-modal"
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={running}>
              Vazgeç
            </Button>
            {/* A submit button of the dialog's form (form attribute): Enter in
                either field submits, like a click. */}
            <Button
              variant="primary"
              type="submit"
              form={FORM_ID}
              disabled={!canAdjust || inputInvalid}
              loading={running}
              data-testid="user-open-order-limit-confirm"
            >
              {mode === 'clear' ? 'Varsayılana dön' : 'Onayla ve kaydet'}
            </Button>
          </>
        }
      >
        {mode !== null && ctx ? (
          <form
            id={FORM_ID}
            className="uinspect-modal-body"
            onSubmit={onConfirm}
            noValidate
            aria-label={mode === 'clear' ? 'Özel limiti kaldır' : 'Açık sipariş limitini değiştir'}
          >
            {mode === 'edit' ? (
              <Field
                label="Yeni limit"
                htmlFor="oolLimit"
                help={`Varsayılandan (${capText(ctx.defaultCap)}) büyük, en fazla ${fmt(LIMIT_MAX)} olan bir tam sayı.`}
                error={dirty && inputInvalid ? rangeError(ctx.defaultCap) : undefined}
              >
                <Input
                  id="oolLimit"
                  type="number"
                  inputMode="numeric"
                  min={ctx.defaultCap + 1}
                  max={LIMIT_MAX}
                  step={1}
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setDirty(true);
                  }}
                  onBlur={() => setDirty(true)}
                  invalid={dirty && inputInvalid}
                  autoComplete="off"
                  disabled={running}
                />
              </Field>
            ) : null}
            <p className="uinspect-modal-text" data-testid="user-open-order-limit-summary">
              {mode === 'clear'
                ? `Özel limit kaldırılacak; hesap varsayılan limite (${capText(ctx.defaultCap)}) döner. Değişiklik hemen geçerli olur ve kayda geçer.`
                : parsed === null
                  ? `Şu anki limit: ${capText(ctx.effective)}.`
                  : parsed === ctx.effective
                    ? `Bu hesabın açık sipariş limiti zaten ${fmt(parsed)}. Kaydetmek değeri değiştirmez, yalnızca kayda geçer.`
                    : `Bu hesabın açık sipariş limiti ${capText(ctx.effective)} → ${fmt(parsed)} olacak. Değişiklik hemen geçerli olur ve kayda geçer.`}
            </p>
            {lowers ? (
              <Banner tone="warn" testId="user-open-order-limit-lower-warning">
                Limit düşürülürse açık siparişler iptal edilmez; yalnızca yeni siparişler reddedilir.
              </Banner>
            ) : null}
            <Field label="Not (isteğe bağlı)" htmlFor="oolReason" help="Kayda geçer; en fazla 500 karakter.">
              <Input
                id="oolReason"
                type="text"
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                placeholder="örn. müşteri talebi #1234"
                autoComplete="off"
                disabled={running}
              />
            </Field>
            {mut.state.kind === 'error' || mut.state.kind === 'auth_required' ? (
              <div className="uinspect-feedback">
                <Banner tone="danger" testId="user-open-order-limit-error">
                  {limitErrorText(mut.state.kind === 'error' ? mut.state.reason : 'auth_required', errorDefault)}
                </Banner>
              </div>
            ) : null}
          </form>
        ) : null}
      </Modal>
    </Card>
  );
}
